// Shared dashboard primitives — FE-031.
//
// Every Phase-1.3 widget (FE-032..FE-037) composes against these
// primitives so loading / error / empty states stay visually
// consistent without each widget reinventing the chrome.

import { AlertCircle } from "lucide-react";
import { type ReactNode } from "react";

import { Card, EmptyState, Skeleton } from "@/design-system";

export interface DashboardCardProps {
  /** Optional section heading (rendered above the card body). */
  title?: ReactNode;
  /** Optional subtitle / description rendered next to / below the title. */
  subtitle?: ReactNode;
  /** Optional right-side action slot (typically a filter or link). */
  actions?: ReactNode;
  /** Optional ID slug for ``data-section`` so widget tests can locate it. */
  section?: string;
  className?: string;
  children: ReactNode;
}

export function DashboardCard({
  title, subtitle, actions, section, className, children,
}: DashboardCardProps) {
  return (
    <Card
      data-section={section}
      className={["flex flex-col", className].filter(Boolean).join(" ")}
    >
      {(title || actions) && (
        <DashboardSectionHeader
          title={title}
          subtitle={subtitle}
          actions={actions}
        />
      )}
      <div className={title || actions ? "mt-3" : ""}>{children}</div>
    </Card>
  );
}

export interface DashboardSectionHeaderProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function DashboardSectionHeader({
  title, subtitle, actions, className,
}: DashboardSectionHeaderProps) {
  return (
    <div
      className={[
        "flex items-start justify-between gap-3",
        className,
      ].filter(Boolean).join(" ")}
    >
      <div>
        {title && (
          <h3 className="text-md font-semibold text-text-primary">{title}</h3>
        )}
        {subtitle && (
          <p className="mt-0.5 text-sm text-text-secondary">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function DashboardLoadingState({
  variant = "card",
  rows = 3,
}: {
  variant?: "card" | "list" | "table";
  rows?: number;
}) {
  return (
    <div data-testid="dashboard-loading" role="status" aria-live="polite">
      <span className="sr-only">Loading dashboard data…</span>
      <Skeleton variant={variant} count={rows} />
    </div>
  );
}

export interface DashboardErrorStateProps {
  title?: ReactNode;
  description?: ReactNode;
  retry?: () => void;
  retryLabel?: string;
}

export function DashboardErrorState({
  title = "Couldn't load this section",
  description = "Refresh to try again or check the network connection.",
  retry,
  retryLabel = "Retry",
}: DashboardErrorStateProps) {
  return (
    <div
      data-testid="dashboard-error"
      role="alert"
      className="flex items-start gap-3 rounded-lg bg-recessed p-4 text-sm text-text-primary"
    >
      <AlertCircle
        size={18}
        className="mt-0.5 flex-shrink-0 stroke-ember"
        aria-hidden="true"
      />
      <div className="flex-1">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="mt-0.5 text-sm text-text-secondary">{description}</p>
        )}
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="mt-2 text-sm font-medium text-teal underline-offset-2 hover:underline"
          >
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export interface DashboardEmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export function DashboardEmptyState(props: DashboardEmptyStateProps) {
  return (
    <div data-testid="dashboard-empty">
      <EmptyState {...props} />
    </div>
  );
}
